// Modèle de tarification résolu par le formulaire d'édition de compte
// (EditAccountModal). Module PUR pour rester testable sans monter tout
// TradingAccountsPage.tsx (qui tire wasp/client/operations → casse sous vitest).
//
// POURQUOI CE FICHIER. `resolveAccountTypeAndTier(template, balance, null, tierOverrideId)`
// avec un accountTypeId à `null` retombe sur le PREMIER type de compte actif du
// template (voir `resolveAccountType` dans propFirmEngine.ts) dès que le
// template a plusieurs types actifs. Un compte Lightning (direct funded) dont
// le template porte aussi un type Standard (one_time) se voyait donc résolu en
// Standard : la bascule "challenge caché" ne se déclenchait jamais côté
// Lightning (le serveur refuse en 400), et un compte Standard légitime en
// challenge pouvait à l'inverse se voir refuser l'option à tort si le premier
// type actif du template était direct funded.
//
// Le serveur (updateAccount, operations.ts ~ligne 1254) ne se pose jamais la
// question : il envoie TOUJOURS `existingAccount.propFirmAccountTypeId` à
// `buildSnapshotFields`, jamais un id dérivé du formulaire. Ce module mirrore
// exactement ce choix côté client.

import { resolveAccountTypeAndTier, type PropFirmTemplateSnapshot } from "./propFirmEngine";
import { accountPricingModel, normalizePricingModel, type PricingModel } from "./pricingModel";

export interface EditFormPricingModelAccount {
  ruleSnapshot?: unknown;
  propFirmAccountType?: { pricingModel?: string | null } | null;
  propFirmAccountTypeId?: string | null;
  propFirmTemplateId: string | null;
  propFirmTierOverrideId: string | null;
  initialBalance: number;
}

export interface EditFormPricingModelForm {
  template: PropFirmTemplateSnapshot | null;
  tierOverrideId: string | null;
  balance: number;
}

/**
 * Résout le modèle de tarification à afficher dans le formulaire d'édition.
 *
 * - Tant que le formulaire n'a rien changé (même firme, même palier, même
 *   solde que le compte en base), on relit `accountPricingModel(account)` :
 *   c'est le point d'entrée unique côté serveur, et il fait aussi autorité sur
 *   le snapshot figé (qui peut différer du type de compte lié si celui-ci a
 *   changé de modèle depuis l'achat).
 * - Dès que l'utilisateur touche firme/palier/solde, le snapshot affiché
 *   n'existe pas encore (il ne sera reposé qu'à la sauvegarde) : on résout
 *   alors "à chaud" avec `resolveAccountTypeAndTier`, en gardant
 *   `account.propFirmAccountTypeId` comme ancre — EXACTEMENT ce que fait le
 *   serveur dans `updateAccount` — plutôt que de laisser tomber sur le premier
 *   type actif du template.
 */
export function editFormPricingModel(
  account: EditFormPricingModelAccount,
  form: EditFormPricingModelForm,
): PricingModel {
  const unchanged =
    form.template?.id === account.propFirmTemplateId &&
    form.tierOverrideId === account.propFirmTierOverrideId &&
    form.balance === account.initialBalance;

  if (unchanged) return accountPricingModel(account);

  if (!form.template) return "one_time";

  const { accountType } = resolveAccountTypeAndTier(
    form.template,
    form.balance,
    account.propFirmAccountTypeId ?? null,
    form.tierOverrideId,
  );
  return normalizePricingModel(accountType?.pricingModel);
}
